// Imports
import gsap from 'gsap';

class Hotspot {
    constructor() {
        this.desktopBreakpoint = 768;
        this.desktopGap = 0;
        this.desktopPadding = 16;
    }
    build() {
        const hotspots = document.querySelectorAll('.hotspot');

        hotspots.forEach((item, i) => {
            this._init(item);
        });
    }
    _init(hotspot) {
        const NAV_OPEN_CLASS = '_nav-open';
        const isAnchoredHotspot = hotspot.classList.contains('hotspot--anchored');
        const desktopBreakpoint = this.desktopBreakpoint;
        const desktopGap = this.desktopGap;
        const desktopPadding = this.desktopPadding;

        const navBtnsContainer = hotspot.querySelector('.hotspot-nav-btns');
        const navBtns = hotspot.querySelectorAll('.hotspot-nav-btn');
        const navToggle = hotspot.querySelector('.hotspot-nav-toggle');
        const pins = hotspot.querySelectorAll('.pin');
        const closeContentBtns = hotspot.querySelectorAll('.hotspot-content-close');
        const frame = hotspot.querySelector('.hotspot-frame');
        let currentId = null;

        if (isAnchoredHotspot) {
            window.addEventListener('resize', () => {
                let activeContent = hotspot.querySelector('.hotspot-content._active');
                let activePin = hotspot.querySelector('.pin._active');

                if (!activeContent || !activePin) {
                    clearDesktopPosition(activeContent);
                    return;
                }

                positionDesktopContent(activeContent, activePin);
            });
        }

        // Toggle button
        if (navToggle) {
            navToggle.addEventListener('click', () => {
                toggleNav();
            });
        }

        // Pins
        if (pins.length) {
            pins.forEach((item, i) => {
                pinClick(item);
            });
        }

        // Nav buttons
        if (navBtns.length) {
            navBtns.forEach((item, i) => {
                pinClick(item);
            });
        }

        closeContentBtns.forEach((item) => {
            item.addEventListener('click', () => {
                closeContent();
            });
        });

        function toggleNav() {
            if (hotspot.classList.contains(NAV_OPEN_CLASS)) {
                closeNav();
            }
            else {
                openNav();
            }
        }
        function openNav() {
            hotspot.classList.add(NAV_OPEN_CLASS);

            gsap.to(
                navBtnsContainer,
                {
                    duration: .3,
                    display: 'block',
                    alpha: 1,
                    onComplete: () => {
                        gsap.to(
                            navBtns,
                            {
                                duration: .35,
                                x: 0,
                                alpha: 1,
                                stagger: .08
                            }
                        );
                    }
                }
            );
        }
        function closeNav() {
            hotspot.classList.remove(NAV_OPEN_CLASS);

            gsap.to(
                navBtnsContainer,
                {
                    duration: .2,
                    alpha: 0,
                    onComplete: () => {
                        gsap.to(
                            navBtnsContainer,
                            {
                                duration: 0,
                                display: 'none',
                            }
                        );

                        gsap.to(
                            navBtns,
                            {
                                duration: 0,
                                x: '20px',
                                alpha: 0,
                            }
                        );
                    }
                }
            );
        }
        function pinClick(item) {
            let id = item.getAttribute('data-hotspot-id');

            item.addEventListener('click', () => {
                showContent(id);
            });
        }
        function closeContent() {
            let currentContent = hotspot.querySelector('.hotspot-content._active'),
                currentPin = hotspot.querySelector('.pin._active');

            // Remove current content
            if (currentContent) {
                let currentContentLines = currentContent.querySelectorAll('.content-item');

                gsap.to(
                    currentContent,
                    {
                        duration: .4,
                        display: 'none',
                        alpha: 0,
                        onComplete: () => {
                            currentContent.classList.remove('_active');

                            currentId = null;

                            gsap.to(
                                currentContent,
                                {
                                    duration: 0,
                                    display: 'none',
                                }
                            );

                            gsap.to(
                                currentContentLines,
                                {
                                    duration: 0,
                                    y: '20px',
                                    alpha: 0,
                                }
                            );
                        }
                    }
                );
            }

            if (currentPin) {
                currentPin.classList.remove('_active');
            }
        }
        function showContent(id) {
            if (id == currentId) {
                return;
            }

            let currentContent = hotspot.querySelector('.hotspot-content._active'),
                content = hotspot.querySelector('[data-hotspot-content="'+id+'"]'),
                pin = hotspot.querySelector('.pin[data-hotspot-id="'+id+'"]');
            if (!content || !pin) {
                return;
            }

            let contentLines = content.querySelectorAll('.content-item');

            // Close nav
            if (window.innerWidth < 768) {
                closeNav();
            }

            // Close content
            closeContent();

            currentId = id;

            // Active pin
            pin.classList.add('_active');

            positionDesktopContent(content, pin);

            // Show new content
            gsap.to(
                content,
                {
                    duration: .4,
                    delay: currentContent ? .2 : 0,
                    display: 'block',
                    alpha: 1,
                    onComplete: () => {
                        content.classList.add('_active');

                        gsap.to(
                            contentLines,
                            {
                                duration: .3,
                                y: 0,
                                alpha: 1,
                                stagger: 0.1
                            }
                        );
                    }
                }
            );
        }
        function positionDesktopContent(content, pin) {
            if (!isAnchoredHotspot || !frame) {
                return;
            }

            if (window.innerWidth < desktopBreakpoint) {
                clearDesktopPosition(content);
                return;
            }

            const previousDisplay = content.style.display;
            const previousVisibility = content.style.visibility;
            const previousOpacity = content.style.opacity;

            content.style.display = 'block';
            content.style.visibility = 'hidden';
            content.style.opacity = '0';

            const frameRect = frame.getBoundingClientRect();
            const pinRect = pin.getBoundingClientRect();
            const contentRect = content.getBoundingClientRect();
            const maxWidth = Math.min(360, Math.max(frameRect.width - (desktopPadding * 2), 220));
            const clampedWidth = Math.min(maxWidth, contentRect.width || maxWidth);
            const pinCenterX = (pinRect.left - frameRect.left) + (pinRect.width / 2);
            const pinCenterY = (pinRect.top - frameRect.top) + (pinRect.height / 2);
            const prefersLeft = pinCenterX < (frameRect.width / 2);
            const prefersTop = pinCenterY < (frameRect.height / 2);
            const desiredLeft = prefersLeft
                ? pinCenterX + desktopGap
                : pinCenterX - clampedWidth - desktopGap;
            const desiredTop = prefersTop
                ? pinCenterY + desktopGap
                : pinCenterY - contentRect.height - desktopGap;
            const left = Math.min(
                Math.max(desiredLeft, desktopPadding),
                Math.max(frameRect.width - clampedWidth - desktopPadding, desktopPadding)
            );
            const top = Math.min(
                Math.max(desiredTop, desktopPadding),
                Math.max(frameRect.height - contentRect.height - desktopPadding, desktopPadding)
            );

            content.style.width = `${clampedWidth}px`;
            content.style.left = `${left}px`;
            content.style.top = `${top}px`;
            content.style.bottom = 'auto';
            content.style.transform = 'none';
            content.style.visibility = previousVisibility;
            content.style.opacity = previousOpacity;
            content.style.display = previousDisplay;
        }
        function clearDesktopPosition(content) {
            if (!content) {
                return;
            }

            content.style.width = '';
            content.style.left = '';
            content.style.top = '';
            content.style.bottom = '';
            content.style.transform = '';
        }
    }
}

export default new Hotspot();
